import { Button, Divider, Flex, Input, Select } from 'antd';
import type { TrapGroup } from '@features/regional-overview/digital-traps/domain';
import { DIGITAL_TRAPS_NETWORK_SELECT } from '@features/regional-overview/digital-traps/domain/tests-id.constants';
import { mapTrapItemToOption } from '@features/regional-overview/digital-traps/utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNetworkSelect } from './network-select.hook';
import { createCustomOption } from './network-select.utils';

interface NetworkSelectProps {
  onChange: (selected: TrapGroup) => void;
}

export const NetworkSelect = ({ onChange }: NetworkSelectProps) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);

  const [t] = useTranslation();
  const { options, setItems } = useNetworkSelect();

  const onSelectChange = (key: string, selected: TrapGroup) => {
    setSelectedKey(key);
    onChange(selected);
  };

  const addItem = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      return;
    }

    setItems(items => [...items, trimmedName]);

    onSelectChange(trimmedName, createCustomOption(trimmedName));

    setOpen(false);
    setName('');
  };

  const handleAddItem = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.preventDefault();
    addItem();
  };

  const handlePressEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    addItem();
  };

  const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  return (
    <Select
      data-testid={DIGITAL_TRAPS_NETWORK_SELECT}
      placeholder={t('pages.regional_overview.digital_traps.network_management.select_placeholder')}
      onSelect={onSelectChange}
      value={selectedKey}
      open={open}
      size='large'
      onOpenChange={setOpen}
      popupRender={menu => (
        <>
          {menu}
          <Divider style={{ margin: '8px 0' }} />
          <Flex style={{ padding: '0px 8px 4px' }}>
            <Input
              placeholder={t('pages.regional_overview.digital_traps.network_management.select_input_placeholder')}
              value={name}
              onChange={onNameChange}
              onPressEnter={handlePressEnter}
              onKeyDown={e => e.stopPropagation()}
            />
            <Button type='link' onClick={handleAddItem} disabled={!name.trim()}>
              {t('pages.regional_overview.digital_traps.network_management.select_add_button')}
            </Button>
          </Flex>
        </>
      )}
      options={options.map(mapTrapItemToOption)}
    />
  );
};
